import { useState } from 'react';
import {
  Download,
  FileSpreadsheet,
  FileText,
  Loader2,
  Lock,
  Users,
  User,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useApp } from '../../context/AppContext';
import { useAPIStorage } from '../../hooks/useAPIStorage';
import {
  exportToPayrollCSV,
  exportToYearlyCSV,
  exportToDailyCSV,
  exportToPDF,
  exportLohnbueroCSV,
  exportGFCSV,
  type NewExportEmployeeInput,
} from '../../utils/payrollExport';
import {
  calculateResults,
  recalcFutureVacationHours,
  applyVacationRecalc,
  applyVacationRecalcMap,
  type VacationRecalcEntry,
} from '../../utils/calculations';
import {
  getPayrollPeriod,
  getPayrollPeriodsForYear,
  type PayrollPeriod,
} from '../../utils/payrollPeriod';
import {
  getVacationFinalizationState,
  type VacationFinalizationUpdate,
} from '../../utils/vacationFinalization';
import { DEFAULT_SETTINGS } from '../../types';
import type { UserEntry, MonthlyApproval, HistorySegment, DayEntry } from '../../types';

interface Props {
  users: UserEntry[];
}

/** Was ein Klick auf "Urlaubsstunden festsetzen" schreiben würde. */
interface FinalizePreview {
  period: PayrollPeriod;
  candidates: Array<{ userId: string; name: string; updates: VacationFinalizationUpdate[] }>;
  dayCount: number;
  provisionalCount: number;
  withoutBasisCount: number;
  finalizedCount: number;
  approvedSkipped: number;
}

export function HRExportPanel({ users }: Props) {
  const { state } = useApp();
  const { getUserData, getApprovals, getPayrollSegments, finalizeVacationHours } = useAPIStorage();
  const [selectedUserId, setSelectedUserId] = useState<string>('all');
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [finalizePreview, setFinalizePreview] = useState<FinalizePreview | null>(null);
  const [finalizeResult, setFinalizeResult] = useState<string | null>(null);
  const [finalizeError, setFinalizeError] = useState<string | null>(null);

  const year = state.year;
  const periods = getPayrollPeriodsForYear(year);
  const selectedUser = users.find((u) => u.id === selectedUserId);
  const currentPeriod = getPayrollPeriod(year, selectedMonth);

  // Mitarbeiter filtern für Sammel-Export
  const exportableUsers = users.filter((u) => {
    if (u.excludeFromExport) return false;
    if (u.employmentEnd) {
      const endDate = new Date(u.employmentEnd);
      if (endDate < currentPeriod.start) return false;
    }
    if (u.employmentStart) {
      const startDate = new Date(u.employmentStart);
      if (startDate > currentPeriod.end) return false;
    }
    return true;
  });

  /** Lädt Entries+Settings für einen User, inklusive Vorjahr für den §11-Lookback. */
  async function loadUserData(userId: string) {
    const data = await getUserData(userId, year);
    const period = getPayrollPeriod(year, selectedMonth);
    let entries = data?.entries || {};
    let previousYearEntries: Record<string, DayEntry> = {};

    // Vorjahresdaten werden aus zwei Gründen gebraucht: der Januar-Zeitraum
    // beginnt am 21.12., und das 91-Tage-Fenster des 13-Wochen-Durchschnitts
    // reicht bei Urlaubsblöcken im ersten Quartal bis ins Vorjahr zurück.
    // Ohne diesen Lookback wird das Urlaubsentgelt dort zu niedrig berechnet.
    const spansPreviousYear = period.start.getFullYear() < year;
    if (spansPreviousYear || selectedMonth <= 4) {
      const prevData = await getUserData(userId, year - 1);
      previousYearEntries = prevData?.entries || {};
      if (spansPreviousYear) {
        entries = { ...previousYearEntries, ...entries };
      }
    }

    return {
      entries,
      previousYearEntries,
      settings: data?.settings || DEFAULT_SETTINGS,
    };
  }

  /** Lädt Daten für alle exportierbaren Mitarbeiter + Freigaben parallel. */
  async function loadAllUsersDataWithApprovals(): Promise<{
    employees: NewExportEmployeeInput[];
    previousYearEntriesByUser: Map<string, Record<string, DayEntry>>;
  }> {
    const [allData, approvals] = await Promise.all([
      Promise.all(
        exportableUsers.map(async (u) => {
          const loaded = await loadUserData(u.id);
          return { user: u, ...loaded };
        })
      ),
      getApprovals(year, selectedMonth),
    ]);

    const approvalMap = new Map<string, MonthlyApproval>();
    for (const a of approvals) {
      approvalMap.set(a.userId, a);
    }

    const previousYearEntriesByUser = new Map<string, Record<string, DayEntry>>();
    const employees = allData.map(({ user: u, entries, previousYearEntries, settings }) => {
      previousYearEntriesByUser.set(u.id, previousYearEntries);
      return {
        userId: u.id,
        firstName: u.firstName,
        lastName: u.lastName,
        personnelNumber: u.personnelNumber,
        employmentType: u.employmentType,
        department: u.department,
        rv: u.rv,
        hansefit: u.hansefit,
        contractualHours: u.contractualHours,
        wageType: u.wageType,
        monthlySalary: u.monthlySalary,
        employmentStart: u.employmentStart,
        employmentEnd: u.employmentEnd,
        entries,
        settings,
        approval: approvalMap.get(u.id) ?? null,
      };
    });

    return { employees, previousYearEntriesByUser };
  }

  /**
   * Schreibt die neu berechneten Urlaubsstunden in die Datenbank, bevor
   * exportiert wird. Ohne diesen Schritt existieren die Werte nur im Speicher
   * des Browsers: die CSV enthielte Zahlen, die in der Datenbank nirgends
   * stehen und sich später nicht gegen die Meldung prüfen lassen.
   *
   * Bewusst nicht geschrieben werden:
   * - Tage außerhalb des exportierten Abrechnungszeitraums,
   * - vorläufige Werte in der Zukunft (der Durchschnitt kann sich noch ändern),
   * - Tage ohne Arbeitsdatum im 91-Tage-Fenster — dort entstünde eine
   *   finalisierte Null, die keine Neuberechnung mehr korrigieren kann,
   * - bereits festgeschriebene Tage,
   * - Monate mit erteilter Abrechnungsfreigabe.
   */
  async function finalizeVacationHoursForExport(
    employees: NewExportEmployeeInput[],
    recalcMaps: Map<string, Map<string, VacationRecalcEntry>>,
    period: PayrollPeriod
  ): Promise<void> {
    const calls = employees.flatMap((emp) => {
      if (emp.approval?.approved) return [];
      const recalcMap = recalcMaps.get(emp.userId);
      if (!recalcMap) return [];

      const { open } = getVacationFinalizationState(emp.entries, recalcMap, period);
      if (open.length === 0) return [];
      return [finalizeVacationHours(emp.userId, open)];
    });

    if (calls.length === 0) return;

    try {
      await Promise.all(calls);
    } catch (e) {
      throw new Error(
        'Urlaubsstunden konnten nicht festgeschrieben werden — Export abgebrochen, ' +
          'damit die Meldung nicht vom Datenbankstand abweicht. ' +
          (e instanceof Error ? e.message : '')
      );
    }
  }

  const nameOf = (userId: string) => {
    const u = users.find((x) => x.id === userId);
    if (!u) return userId;
    return u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.name;
  };

  /**
   * Ermittelt den Umfang des Festsetzens, ohne zu schreiben.
   *
   * Bewusst zweistufig: das Festsetzen ist ein Schreibzugriff auf die Lohndaten
   * aller Mitarbeiter des Zeitraums. Wie viele Tage bei wie vielen Personen
   * betroffen sind, muss vor dem Klick sichtbar sein und nicht danach.
   */
  const handleFinalizePreview = async () => {
    setLoading('finalize');
    setError(null);
    setFinalizeResult(null);
    setFinalizeError(null);

    try {
      const period = getPayrollPeriod(year, selectedMonth);
      const { employees, previousYearEntriesByUser } = await loadAllUsersDataWithApprovals();

      const candidates: FinalizePreview['candidates'] = [];
      let provisionalCount = 0;
      let withoutBasisCount = 0;
      let finalizedCount = 0;
      let approvedSkipped = 0;

      for (const emp of employees) {
        const recalcMap = recalcFutureVacationHours(
          emp.entries,
          previousYearEntriesByUser.get(emp.userId)
        );
        const state = getVacationFinalizationState(emp.entries, recalcMap, period);
        provisionalCount += state.provisionalCount;
        withoutBasisCount += state.withoutBasisCount;
        finalizedCount += state.finalizedCount;

        // Freigegebene Monate bleiben unberührt: dort würde jetzt der heute
        // berechnete Wert festgeschrieben, den das inzwischen gewanderte
        // 13-Wochen-Fenster verändert hat — also nicht die Zahl, die damals
        // gemeldet wurde. Ein falscher Nachweis ist schlechter als keiner.
        // Sichtbar zählen statt still überspringen.
        if (emp.approval?.approved) {
          approvedSkipped += state.open.length;
          continue;
        }
        if (state.open.length === 0) continue;

        candidates.push({
          userId: emp.userId,
          name: nameOf(emp.userId),
          updates: state.open,
        });
      }

      setFinalizePreview({
        period,
        candidates,
        dayCount: candidates.reduce((sum, c) => sum + c.updates.length, 0),
        provisionalCount,
        withoutBasisCount,
        finalizedCount,
        approvedSkipped,
      });
    } catch (e) {
      console.error('Festsetzen-Vorschau fehlgeschlagen:', e);
      setError(e instanceof Error ? e.message : 'Umfang konnte nicht ermittelt werden');
    } finally {
      setLoading(null);
    }
  };

  const handleFinalizeConfirm = async () => {
    if (!finalizePreview) return;
    const { candidates, dayCount, period } = finalizePreview;

    setLoading('finalize');
    setError(null);
    setFinalizeError(null);

    // Bewusst allSettled statt all: die Schreibvorgänge laufen pro Mitarbeiter.
    // Bricht einer ab, sind die anderen trotzdem geschrieben. Ein pauschales
    // "fehlgeschlagen" würde einen Teilstand als Totalausfall darstellen — HR
    // wüsste nicht, welche Zahlen jetzt festliegen.
    const results = await Promise.allSettled(
      candidates.map((c) => finalizeVacationHours(c.userId, c.updates))
    );
    setLoading(null);

    const failed = candidates.filter((_, i) => results[i]!.status === 'rejected');
    const writtenDays = candidates
      .filter((_, i) => results[i]!.status === 'fulfilled')
      .reduce((sum, c) => sum + c.updates.length, 0);

    if (failed.length === 0) {
      setFinalizePreview(null);
      setFinalizeResult(
        `${dayCount} Urlaubstag${dayCount === 1 ? '' : 'e'} bei ` +
          `${candidates.length} Mitarbeiter${candidates.length === 1 ? '' : 'n'} ` +
          `für ${period.label} festgesetzt.`
      );
      return;
    }

    const firstError = results.find((r) => r.status === 'rejected');
    console.error('Festsetzen teilweise fehlgeschlagen:', failed.map((c) => c.name), firstError);

    // Dialog offen lassen: er zeigt, um welchen Umfang es ging. Der Endpunkt
    // schreibt alle Tage eines Mitarbeiters in einer Transaktion, ein
    // gescheiterter Mitarbeiter hat also keinen Teilstand. Erneutes Festsetzen
    // schreibt bereits gesetzte Tage mit demselben Wert nochmal — der Hinweis
    // muss das sagen, sonst traut sich niemand ein zweites Mal.
    setFinalizeError(
      `${writtenDays} von ${dayCount} Tagen festgesetzt. Fehlgeschlagen: ` +
        `${failed.map((c) => c.name).join(', ')} — dort wurde nichts geschrieben, ` +
        'die Schreibvorgänge sind pro Mitarbeiter atomar. Erneutes Festsetzen ist ' +
        'unschädlich: bereits gesetzte Tage bekommen denselben Wert erneut.'
    );
  };

  const handleExport = async (type: 'lohnbuero' | 'gf' | 'payroll-month' | 'payroll-year' | 'daily' | 'pdf') => {
    setLoading(type);
    setError(null);
    setFinalizeResult(null);

    try {
      if (type === 'lohnbuero' || type === 'gf') {
        const period = getPayrollPeriod(year, selectedMonth);
        const pad = (n: number) => n.toString().padStart(2, '0');
        const fromStr = `${period.start.getFullYear()}-${pad(period.start.getMonth() + 1)}-${pad(period.start.getDate())}`;
        const toStr = `${period.end.getFullYear()}-${pad(period.end.getMonth() + 1)}-${pad(period.end.getDate())}`;

        // Segments + Employee-Daten parallel laden
        const [{ employees, previousYearEntriesByUser }, segmentsMap] = await Promise.all([
          loadAllUsersDataWithApprovals(),
          getPayrollSegments(fromStr, toStr).catch((err): Record<string, HistorySegment[]> => {
            console.warn('Segment-Daten konnten nicht geladen werden, Fallback auf Standard:', err);
            return {};
          }),
        ]);

        // Urlaubsstunden nach §11 BUrlG neu berechnen und anschließend in der
        // Datenbank festschreiben. Die Reihenfolge ist wichtig: erst schreiben,
        // dann exportieren — schlägt das Schreiben fehl, bricht der Export ab.
        const recalcMaps = new Map<string, Map<string, VacationRecalcEntry>>();
        const exportData = employees.map((emp) => {
          const recalcMap = recalcFutureVacationHours(
            emp.entries,
            previousYearEntriesByUser.get(emp.userId)
          );
          recalcMaps.set(emp.userId, recalcMap);
          return { ...emp, entries: applyVacationRecalcMap(emp.entries, recalcMap) };
        });

        await finalizeVacationHoursForExport(employees, recalcMaps, period);

        if (type === 'lohnbuero') {
          exportLohnbueroCSV(exportData, year, selectedMonth, segmentsMap);
        } else {
          exportGFCSV(exportData, year, selectedMonth, segmentsMap);
        }
      } else {
        // Einzel-Export: Daten für ausgewählten User laden
        if (!selectedUser) {
          // Loading-State freigeben und erklaeren, warum der Export nicht laeuft.
          setLoading(null);
          setError('Bitte zuerst einen Mitarbeiter auswaehlen.');
          return;
        }
        const { entries, previousYearEntries, settings } = await loadUserData(selectedUser.id);
        const userName = selectedUser.firstName && selectedUser.lastName
          ? `${selectedUser.firstName} ${selectedUser.lastName}`
          : selectedUser.name;

        // §11 BUrlG auch in den Einzel-Exporten anwenden. Bisher galt die
        // Neuberechnung nur für Lohnbüro und GF, sodass PDF, Monats- und
        // Jahres-CSV desselben Mitarbeiters andere Stunden auswiesen.
        // Diese Exporte schreiben nichts fest — sie sind keine Lohnmeldung.
        const recalcEntries = applyVacationRecalc(entries, previousYearEntries);

        switch (type) {
          case 'payroll-month':
            exportToPayrollCSV(recalcEntries, year, selectedMonth, settings, userName);
            break;
          case 'payroll-year':
            exportToYearlyCSV(recalcEntries, year, settings, userName);
            break;
          case 'daily':
            exportToDailyCSV(recalcEntries, year);
            break;
          case 'pdf': {
            const calcResults = calculateResults(recalcEntries, settings, year, new Date(), 0, previousYearEntries, 0, selectedUser.employmentStart, selectedUser.employmentEnd);
            await exportToPDF(recalcEntries, year, settings, userName, calcResults);
            break;
          }
        }
      }
    } catch (e) {
      console.error('Export-Fehler:', e);
      setError(e instanceof Error ? e.message : 'Fehler beim Export');
    } finally {
      setLoading(null);
    }
  };

  const isExporting = loading !== null;

  return (
    <Card>
      <CardContent className="p-5">
      <div className="flex items-center gap-3 mb-4">
        <div className="rounded-md bg-green-50 dark:bg-green-900/30 p-1.5">
          <Download className="h-4 w-4 text-green-600 dark:text-green-400" />
        </div>
        <div>
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-50">Lohn-Export</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Daten für Lohnbuchhaltung und Geschäftsführung
          </p>
        </div>
      </div>

      {/* Auswahl: Mitarbeiter + Monat */}
      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <div>
          <Label htmlFor="export-employee" className="text-xs text-gray-600 dark:text-gray-400 mb-1">
            Mitarbeiter
          </Label>
          <Select value={selectedUserId} onValueChange={(v) => setSelectedUserId(v)} disabled={isExporting}>
            <SelectTrigger id="export-employee">
              <div className="flex items-center gap-2">
                {selectedUserId === 'all' ? (
                  <Users className="w-3.5 h-3.5 text-gray-400" />
                ) : (
                  <User className="w-3.5 h-3.5 text-gray-400" />
                )}
                <SelectValue placeholder="Alle Mitarbeiter" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Mitarbeiter</SelectItem>
              {users
                .sort((a, b) => (a.lastName ?? a.name).localeCompare(b.lastName ?? b.name, 'de'))
                .map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.name}</SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="export-month" className="text-xs text-gray-600 dark:text-gray-400 mb-1">
            Abrechnungsmonat
          </Label>
          <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(parseInt(v, 10))} disabled={isExporting}>
            <SelectTrigger id="export-month">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {periods.map((p) => (
                <SelectItem key={p.month} value={String(p.month)}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {error && (
        <div className="mb-3 p-2 text-xs border border-red-300/60 bg-red-50 dark:border-red-500/20 dark:bg-red-900/10 rounded-lg text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {finalizeResult && (
        <div className="mb-3 p-2 text-xs border border-green-300/60 bg-green-50 dark:border-green-500/20 dark:bg-green-900/10 rounded-lg text-green-700 dark:text-green-300">
          {finalizeResult}
        </div>
      )}

      {/* Export-Buttons */}
      <div className="space-y-2">
        {/* Sammel-Exporte (nur bei "Alle Mitarbeiter") */}
        {selectedUserId === 'all' && (
          <>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {exportableUsers.length} von {users.length} Mitarbeiter{users.length !== 1 ? 'n' : ''} werden exportiert
              {exportableUsers.length < users.length && (
                <span className="text-gray-400 dark:text-gray-500">
                  {' '}(ausgeschlossen: {users.length - exportableUsers.length})
                </span>
              )}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => handleExport('lohnbuero')}
                disabled={isExporting || exportableUsers.length === 0}
                variant="outline"
                className="flex-1 min-w-[200px] justify-start gap-3 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/40"
              >
                {loading === 'lohnbuero' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <FileSpreadsheet className="w-4 h-4" />
                )}
                Lohnbüro-Export (CSV)
              </Button>
              <Button
                onClick={() => handleExport('gf')}
                disabled={isExporting || exportableUsers.length === 0}
                variant="outline"
                className="flex-1 min-w-[200px] justify-start gap-3 border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40"
              >
                {loading === 'gf' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <FileSpreadsheet className="w-4 h-4" />
                )}
                Geschäftsführungs-Export (CSV)
              </Button>
            </div>

            {/*
              Festsetzen ist absichtlich ein eigener Button und nicht nur ein
              Nebeneffekt des Exports: wer die Zahlen aus der Monatsübersicht
              abschreibt statt zu exportieren, hätte sonst nie einen
              festgeschriebenen Stand in der Datenbank.
            */}
            <div className="pt-2 border-t border-gray-200 dark:border-white/10">
              <Button
                onClick={handleFinalizePreview}
                disabled={isExporting || exportableUsers.length === 0}
                variant="outline"
                size="sm"
                className="gap-2"
              >
                {loading === 'finalize' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Lock className="w-4 h-4" />
                )}
                Urlaubsstunden festsetzen
              </Button>
              <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                Schreibt die Urlaubsstunden nach §11 BUrlG für {currentPeriod.label} in
                der Datenbank fest. Nötig, wenn die Zahlen aus der Monatsübersicht
                abgeschrieben und nicht exportiert werden — sonst ändern sie sich
                nachträglich, sobald Arbeitsstunden nachgetragen werden.
              </p>
            </div>
          </>
        )}

        {/* Einzel-Exporte (nur bei konkretem Mitarbeiter) */}
        {selectedUserId !== 'all' && (
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => handleExport('payroll-month')}
              disabled={isExporting}
              variant="secondary"
              size="sm"
            >
              {loading === 'payroll-month' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
              Lohn-CSV (Monat)
            </Button>
            <Button
              onClick={() => handleExport('payroll-year')}
              disabled={isExporting}
              variant="secondary"
              size="sm"
            >
              {loading === 'payroll-year' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
              Lohn-CSV (Jahr)
            </Button>
            <Button
              onClick={() => handleExport('daily')}
              disabled={isExporting}
              variant="secondary"
              size="sm"
            >
              {loading === 'daily' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
              Tagesdetails
            </Button>
            <Button
              onClick={() => handleExport('pdf')}
              disabled={isExporting}
              variant="secondary"
              size="sm"
            >
              {loading === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              PDF-Bericht
            </Button>
          </div>
        )}
      </div>
      </CardContent>

      <Dialog
        open={finalizePreview !== null}
        onOpenChange={(open) => {
          if (!open) {
            setFinalizePreview(null);
            setFinalizeError(null);
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Urlaubsstunden festsetzen</DialogTitle>
            <DialogDescription>
              {finalizePreview?.period.label}
            </DialogDescription>
          </DialogHeader>

          {finalizePreview && (
            <div className="space-y-3 text-sm">
              {finalizePreview.dayCount === 0 ? (
                <p className="text-gray-600 dark:text-gray-400">
                  In diesem Abrechnungszeitraum gibt es nichts festzusetzen.
                </p>
              ) : (
                <>
                  <p className="text-gray-900 dark:text-gray-100">
                    <strong className="tabular-nums">{finalizePreview.dayCount}</strong>{' '}
                    Urlaubstag{finalizePreview.dayCount === 1 ? '' : 'e'} bei{' '}
                    <strong className="tabular-nums">{finalizePreview.candidates.length}</strong>{' '}
                    Mitarbeiter{finalizePreview.candidates.length === 1 ? '' : 'n'} werden
                    festgeschrieben und ändern sich danach nicht mehr automatisch.
                  </p>
                  <div className="max-h-52 overflow-y-auto rounded-md border border-gray-200 dark:border-white/10 divide-y divide-gray-100 dark:divide-white/5">
                    {finalizePreview.candidates.map((c) => (
                      <div key={c.userId} className="flex items-baseline justify-between gap-3 px-3 py-1.5">
                        <span className="text-gray-900 dark:text-gray-100">{c.name}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums whitespace-nowrap">
                          {c.updates.length} Tag{c.updates.length === 1 ? '' : 'e'}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <ul className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                {finalizePreview.finalizedCount > 0 && (
                  <li>
                    {finalizePreview.finalizedCount} Tage sind bereits festgesetzt und
                    bleiben unverändert.
                  </li>
                )}
                {finalizePreview.provisionalCount > 0 && (
                  <li>
                    {finalizePreview.provisionalCount} Tage liegen in der Zukunft und
                    bleiben vorläufig — der Durchschnitt kann sich noch ändern.
                  </li>
                )}
                {finalizePreview.withoutBasisCount > 0 && (
                  <li>
                    {finalizePreview.withoutBasisCount} Tage haben kein Arbeitsdatum im
                    91-Tage-Fenster. Sie werden nicht auf null festgeschrieben, weil das
                    keine Neuberechnung mehr korrigieren könnte.
                  </li>
                )}
                {finalizePreview.approvedSkipped > 0 && (
                  <li>
                    {finalizePreview.approvedSkipped} Tage gehören zu Mitarbeitern mit
                    erteilter Abrechnungsfreigabe und werden übersprungen — der heute
                    berechnete Wert wäre nicht der damals gemeldete.
                  </li>
                )}
              </ul>

              {finalizeError && (
                <div className="p-2 text-xs border border-red-300/60 bg-red-50 dark:border-red-500/20 dark:bg-red-900/10 rounded-lg text-red-700 dark:text-red-300">
                  {finalizeError}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => { setFinalizePreview(null); setFinalizeError(null); }}
            >
              {finalizeError ? 'Schließen' : 'Abbrechen'}
            </Button>
            <Button
              onClick={handleFinalizeConfirm}
              disabled={loading === 'finalize' || finalizePreview?.dayCount === 0}
            >
              {loading === 'finalize' && <Loader2 className="w-4 h-4 animate-spin" />}
              {finalizeError ? 'Erneut versuchen' : 'Festsetzen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
